<script setup lang="ts">
import type { SpatialData, SpatialDataFile } from '@/types/spatialData'

import { defineEmits, reactive, ref } from 'vue'

import FileUploader from './FileUploader.vue'

const emit = defineEmits(['update:data'])

// 当前已上传的文件数据
const spatialData = reactive<SpatialData>({
  shape_numpy: '',
  style_numpy: '',
  model_settings: '',
})

// 上传的文件列表
const uploadedFiles = ref<SpatialDataFile[]>([])

// 处理文件上传
function handleFileUploaded(fileData: SpatialDataFile) {
  // 根据文件名存储到对应的属性中
  if (fileData.fileName === 'shape.npy') {
    spatialData.shape_numpy = fileData.fileContent
    addToUploadedFiles(fileData)
  }
  else if (fileData.fileName === 'style.npy') {
    spatialData.style_numpy = fileData.fileContent
    addToUploadedFiles(fileData)
  }
  else if (fileData.fileName === 'settings.json') {
    spatialData.model_settings = fileData.fileContent
    // spatialData.model_settings_json = atob(fileData.fileContent)
    addToUploadedFiles(fileData)
  }

  // 向父组件发送更新
  emit('update:data', spatialData)
}

// 将文件添加到上传列表
function addToUploadedFiles(fileData: SpatialDataFile) {
  // 检查是否已存在同名文件，如果存在则更新
  const index = uploadedFiles.value.findIndex(f => f.fileName === fileData.fileName)

  if (index >= 0) {
    uploadedFiles.value[index] = fileData
  }
  else {
    uploadedFiles.value.push(fileData)
  }
}

// 删除已上传的文件
function removeFile(fileName: string) {
  const index = uploadedFiles.value.findIndex(f => f.fileName === fileName)
  if (index >= 0) {
    uploadedFiles.value.splice(index, 1)

    // 同时清除对应的数据
    if (fileName === 'shape.npy') {
      spatialData.shape_numpy = ''
    }
    else if (fileName === 'style.npy') {
      spatialData.style_numpy = ''
    }
    else if (fileName === 'settings.json') {
      spatialData.model_settings = ''
    }

    // 向父组件发送更新
    emit('update:data', spatialData)
  }
}

// 格式化文件大小
function formatFileSize(size: number): string {
  if (size < 1024)
    return `${size} B`
  else if (size < 1024 * 1024)
    return `${(size / 1024).toFixed(2)} KB`
  else return `${(size / (1024 * 1024)).toFixed(2)} MB`
}
</script>

<template>
  <div class="spatial-data-uploader">
    <FileUploader
      accept=".npy,.json"
      @file-uploaded="handleFileUploaded"
    >
      <template #tip>
        <div class="drag-tip">
          支持上传 shape.npy, style.npy 和 settings.json 文件
        </div>
      </template>
    </FileUploader>

    <!-- 已上传文件列表 -->
    <div v-if="uploadedFiles.length > 0" class="uploaded-files">
      <div class="upload-title">
        已上传文件
      </div>
      <el-table :data="uploadedFiles" style="width: 100%">
        <el-table-column prop="fileName" label="文件名" />
        <el-table-column label="大小">
          <template #default="scope">
            {{ formatFileSize(scope.row.fileSize) }}
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" width="120">
          <template #default="scope">
            <el-button
              type="danger"
              size="small"
              @click="removeFile(scope.row.fileName)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<style scoped>
.spatial-data-uploader {
  margin: 20px 0;
}

.uploaded-files {
  margin-top: 20px;
}

.upload-title {
  margin-bottom: 10px;
  font-weight: bold;
}

.drag-tip {
  font-size: 12px;
  color: #909399;
}
</style>
